/**
 * SmartImprove Recommendations — Workstream-driven Business Solution layer
 * (Feb 2026 redesign).
 *
 * Two stacked sections:
 *   A. Primary Recommendations — one per finalised workstream (REC01, ...)
 *   B. Strategic / Accelerator Recommendations — manual, cross-workstream
 *      (SREC01, ...)
 *
 * Recommendations are consultant-owned business outputs. Implementation
 * decomposition, ROI numbers, and phases belong to other tabs and are NOT
 * surfaced here.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  FileText,
  Plus,
  Pencil,
  Archive,
  CheckCircle,
  Trash2,
  Lightbulb,
  Sparkles,
  RefreshCw,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Undo2,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Textarea } from "../../../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { compareFindings } from "../findingOrder";
import { OverlapWarning } from "../OverlapWarning";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ax = axios.create({
  baseURL: `${BACKEND_URL}/api/smartimprove`,
  withCredentials: true,
});

const STATUS_CHIP = {
  draft: "bg-[#F3F4F6] text-[#374151] border-[#E5E7EB]",
  reviewed: "bg-[#EFF6FF] text-[#1D4ED8] border-[#BFDBFE]",
  accepted: "bg-[#ECFDF5] text-[#047857] border-[#A7F3D0]",
  archived: "bg-[#FEE2E2] text-[#991B1B] border-[#FECACA]",
};

export default function RecommendationsTab({
  engagementId,
  issues,
  sites,
  isFinalized = false,
}) {
  const [recs, setRecs] = useState([]);
  const [strategic, setStrategic] = useState([]);
  const [workstreams, setWorkstreams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // primary rec
  const [strategicDialog, setStrategicDialog] = useState(null); // {mode, row?}
  const [generating, setGenerating] = useState(false);
  const [regenConfirm, setRegenConfirm] = useState(false);

  const findingsById = useMemo(() => {
    const m = {};
    (issues || []).forEach((f) => {
      if (f && f.id) m[f.id] = f;
    });
    return m;
  }, [issues]);
  const sitesById = useMemo(() => {
    const m = {};
    (sites || []).forEach((s) => {
      if (s && s.id) m[s.id] = s.warehouse_name;
    });
    return m;
  }, [sites]);
  const workstreamsById = useMemo(() => {
    const m = {};
    (workstreams || []).forEach((w) => {
      if (w && w.id) m[w.id] = w;
    });
    return m;
  }, [workstreams]);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [r1, r2, r3] = await Promise.all([
        ax.get(`/engagements/${engagementId}/recommendations-v2`),
        ax.get(`/engagements/${engagementId}/strategic-recommendations`),
        ax.get(`/engagements/${engagementId}/priva/groups`),
      ]);
      setRecs(r1.data || []);
      setStrategic(r2.data || []);
      // Stage 2 only consumes Accepted workstreams. Archived/Draft are
      // hidden from the linking dropdowns and excluded from generation.
      setWorkstreams((r3.data || []).filter((g) => (g.status || "").toLowerCase() !== "archived"));
    } catch {
      toast.error("Failed to load recommendations");
    } finally {
      setLoading(false);
    }
  }, [engagementId]);

  useEffect(() => {
    reload();
    // One-time legacy archive — safe to call repeatedly (idempotent).
    ax.post(`/engagements/${engagementId}/recommendations-v2/archive-legacy`).catch(
      () => {}
    );
  }, [reload, engagementId]);

  const visibleRecs = useMemo(
    () => (recs || []).filter((r) => r.status !== "archived"),
    [recs]
  );
  const archivedRecs = useMemo(
    () => (recs || []).filter((r) => r.status === "archived"),
    [recs]
  );

  // --------------------------------------------------- primary handlers --
  const setStatus = async (rec, status) => {
    try {
      await ax.post(`/recommendations-v2/${rec.id}/status`, { status });
      toast.success(`Set to ${status}`);
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update status");
    }
  };

  const sendToImpl = null;
  void sendToImpl;

  const handleClassify = async (rec, patch) => {
    try {
      await ax.patch(`/recommendations-v2/${rec.id}`, patch);
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update classification");
    }
  };

  const handleStrategicClassify = async (row, patch) => {
    try {
      await ax.patch(`/strategic-recommendations/${row.id}`, patch);
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update classification");
    }
  };

  const handleStrategicStatus = async (row, status) => {
    try {
      await ax.post(`/strategic-recommendations/${row.id}/status`, { status });
      toast.success(`Set to ${status}`);
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update status");
    }
  };

  const handleEditSave = async ({
    title,
    summary,
    expected_business_impact,
    linked_workstream_ids,
  }) => {
    if (!editing) return;
    try {
      await ax.patch(`/recommendations-v2/${editing.id}`, {
        title,
        summary,
        expected_business_impact,
        linked_workstream_ids,
      });
      toast.success("Recommendation updated");
      setEditing(null);
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  // ------------------------------------------------- strategic handlers --
  const handleStrategicSave = async (payload) => {
    try {
      if (strategicDialog?.row?.id) {
        await ax.patch(`/strategic-recommendations/${strategicDialog.row.id}`, payload);
        toast.success("Updated");
      } else {
        await ax.post(`/engagements/${engagementId}/strategic-recommendations`, payload);
        toast.success("Strategic recommendation added");
      }
      setStrategicDialog(null);
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const handleStrategicDelete = async (row) => {
    if (!window.confirm(`Delete ${row.srec_code || "strategic recommendation"}?`)) return;
    try {
      await ax.delete(`/strategic-recommendations/${row.id}`);
      toast.success("Deleted");
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  if (loading) {
    return <div className="text-xs text-[#6B7280] px-1">Loading recommendations…</div>;
  }

  const acceptedWorkstreams = workstreams.filter(
    (w) => (w.status || "").toLowerCase() === "accepted"
  );

  const confirmGenerate = () => {
    if (acceptedWorkstreams.length === 0) {
      toast.error(
        "No accepted workstreams yet. Open Improvement Insights and Accept at least one workstream first."
      );
      return;
    }
    setRegenConfirm(true);
  };

  const runGenerate = async () => {
    setRegenConfirm(false);
    setGenerating(true);
    try {
      // Stage 2 — Claude-backed Recommendation Generation. Same async
      // poll pattern as Stage 1 because Claude routinely needs 30-60s
      // which exceeds the ingress proxy timeout.
      const start = await ax.post(
        `/engagements/${engagementId}/generate-recommendations`
      );
      const runId = start.data?.run_id;
      if (!runId) throw new Error("Generation did not return a run_id");

      const POLL_INTERVAL_MS = 4000;
      const POLL_MAX_ATTEMPTS = 180; // ~12 minutes ceiling (Claude streams up to ~9 min)
      let final = null;
      for (let i = 0; i < POLL_MAX_ATTEMPTS; i += 1) {
        await new Promise((res) => setTimeout(res, POLL_INTERVAL_MS));
        let run;
        try {
          run = (await ax.get(`/ai-runs/${runId}`)).data;
        } catch (_) {
          continue;
        }
        if (run && run.status && run.status !== "running") {
          final = run;
          break;
        }
      }
      if (!final) {
        toast.error(
          "Recommendation generation is taking longer than usual. Refresh in a moment to see results."
        );
        return;
      }
      if (final.status === "succeeded") {
        const c = final.counts || {};
        toast.success(
          `Generated ${c.primary_recommendations || 0} primary · ${
            c.strategic_recommendations || 0
          } strategic recommendation${
            (c.primary_recommendations || 0) + (c.strategic_recommendations || 0) === 1 ? "" : "s"
          } from ${acceptedWorkstreams.length} accepted workstream${
            acceptedWorkstreams.length === 1 ? "" : "s"
          }`
        );
        await reload();
      } else {
        toast.error(
          final.error_message ||
            "Recommendation generation failed. Existing recommendations are unchanged."
        );
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(
        typeof detail === "string" ? detail : "Failed to generate recommendations"
      );
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div
      className="max-w-6xl mx-auto space-y-6"
      data-testid="smartimprove-recommendations-tab"
    >
      <OverlapWarning engagementId={engagementId} scope="accepted" />
      <GenerateBand
        accepted={acceptedWorkstreams.length}
        totalWorkstreams={workstreams.length}
        existingPrimary={recs.length}
        existingStrategic={strategic.length}
        generating={generating}
        isFinalized={isFinalized}
        onGenerate={confirmGenerate}
      />

      <PrimarySection
        recs={visibleRecs}
        archived={archivedRecs}
        workstreams={workstreams}
        workstreamsById={workstreamsById}
        findingsById={findingsById}
        sitesById={sitesById}
        isFinalized={isFinalized}
        onEdit={(r) => setEditing(r)}
        onStatus={setStatus}
        onClassify={handleClassify}
      />

      <StrategicSection
        items={strategic}
        workstreams={workstreams}
        workstreamsById={workstreamsById}
        findingsById={findingsById}
        sitesById={sitesById}
        isFinalized={isFinalized}
        onAdd={() => setStrategicDialog({ mode: "create" })}
        onEdit={(row) => setStrategicDialog({ mode: "edit", row })}
        onDelete={handleStrategicDelete}
        onStatus={handleStrategicStatus}
        onClassify={handleStrategicClassify}
      />

      {editing ? (
        <EditPrimaryDialog
          rec={editing}
          workstreams={workstreams}
          onClose={() => setEditing(null)}
          onSave={handleEditSave}
        />
      ) : null}
      {strategicDialog ? (
        <StrategicDialog
          mode={strategicDialog.mode}
          row={strategicDialog.row}
          workstreams={workstreams}
          onClose={() => setStrategicDialog(null)}
          onSave={handleStrategicSave}
        />
      ) : null}
      {regenConfirm ? (
        <RegenerateRecsConfirmDialog
          accepted={acceptedWorkstreams.length}
          primary={recs.length}
          strategic={strategic.length}
          onClose={() => setRegenConfirm(false)}
          onConfirm={runGenerate}
        />
      ) : null}
    </div>
  );
}

// ============================================================ Section A --
function PrimarySection({
  recs,
  archived,
  workstreams,
  workstreamsById,
  findingsById,
  sitesById,
  isFinalized,
  onEdit,
  onStatus,
  onClassify,
}) {
  const [expandedId, setExpandedId] = useState(null);
  return (
    <section data-testid="primary-recommendations-section">
      <SectionHeader
        icon={FileText}
        label="Primary Recommendations"
        meta={`${recs.length} active${archived.length ? ` · ${archived.length} archived` : ""}`}
        helper="One business recommendation per consolidated set of accepted workstreams"
      />
      {recs.length === 0 ? (
        <EmptyState
          icon={Lightbulb}
          title="No recommendations generated yet"
          description="Review and accept transformation workstreams first, then let PRIVA generate executive recommendations."
        />
      ) : (
        <div className="space-y-2">
          {recs.map((r) => (
            <PrimaryCard
              key={r.id}
              rec={r}
              expanded={expandedId === r.id}
              onToggle={() =>
                setExpandedId(expandedId === r.id ? null : r.id)
              }
              workstreams={workstreams}
              workstreamsById={workstreamsById}
              findingsById={findingsById}
              sitesById={sitesById}
              isFinalized={isFinalized}
              onEdit={() => onEdit(r)}
              onStatus={(s) => onStatus(r, s)}
              onClassify={(patch) => onClassify(r, patch)}
            />
          ))}
        </div>
      )}
    </section>
  );
}

// Compact "Workstream-style" Primary Recommendation card.
// Header (always visible) follows the InsightsTab pattern: code badge,
// title, status, short summary, linked-workstream pills + finding count.
// Body (expanded only) lists Linked Workstreams → Linked Findings →
// Recommendation Details.
function PrimaryCard({
  rec,
  expanded,
  onToggle,
  workstreamsById,
  findingsById,
  sitesById,
  isFinalized,
  onEdit,
  onStatus,
  onClassify,
}) {
  const status = (rec.status || "draft").toLowerCase();
  const linkedWsIds = (rec.linked_workstream_ids && rec.linked_workstream_ids.length)
    ? rec.linked_workstream_ids
    : (rec.linked_workstream_id ? [rec.linked_workstream_id] : []);
  const linkedWsList = linkedWsIds
    .map((id) => workstreamsById?.[id])
    .filter(Boolean);
  // Fallback to cached codes/titles for archived workstreams the lookup
  // map no longer contains.
  const linkedWsCodes = linkedWsList.length
    ? linkedWsList.map((w) => w.group_code).filter(Boolean)
    : (rec.linked_workstream_codes || []);

  // Union linked findings across every linked workstream (deduped).
  const linkedFindings = useMemo(() => {
    const seen = new Set();
    const out = [];
    const collect = (fid) => {
      if (!fid || seen.has(fid)) return;
      seen.add(fid);
      const f = findingsById?.[fid];
      if (f) out.push(f);
    };
    linkedWsList.forEach((ws) => {
      (ws.linked_finding_ids || []).forEach(collect);
    });
    // Persisted rec.linked_finding_ids is a backup if workstream lookup
    // is empty (e.g. workstream archived).
    if (out.length === 0) {
      (rec.linked_finding_ids || []).forEach(collect);
    }
    out.sort(compareFindings);
    return out;
  }, [linkedWsList, rec, findingsById]);

  const findingCount = linkedFindings.length;

  return (
    <article
      className={`bg-white border rounded-sm transition ${
        expanded
          ? "border-[#025979] shadow-sm"
          : "border-[#E5E7EB] hover:border-[#94A3B8]"
      }`}
      data-testid={`primary-rec-${rec.id}`}
    >
      {/* Collapsed header */}
      <header className="px-4 py-3 flex items-start gap-3">
        <span
          className="shrink-0 text-[10px] font-bold tracking-wider text-[#025979] bg-[#F0F7FA] border border-[#BAE6FD] px-1.5 py-0.5 rounded-sm whitespace-nowrap mt-0.5"
          title="Recommendation code"
        >
          {rec.rec_code}
        </span>
        <button
          type="button"
          onClick={onToggle}
          className="flex-1 min-w-0 text-left"
          data-testid={`primary-rec-toggle-${rec.id}`}
        >
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="text-sm font-semibold text-[#111827]">{rec.title}</h4>
            <span
              className={`inline-flex px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider border rounded-sm ${
                STATUS_CHIP[status] || STATUS_CHIP.draft
              }`}
              data-testid={`primary-rec-status-${rec.id}`}
            >
              {status}
            </span>
            <span
              className="inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded-sm bg-[#F0EDF9] text-[#473F8B] border border-[#DDD6FE] uppercase tracking-wider"
              title="Generated by PRIVA from accepted workstreams"
              data-testid={`primary-rec-priva-${rec.id}`}
            >
              <Sparkles className="w-2.5 h-2.5" strokeWidth={2} />
              PRIVA
            </span>
          </div>
          {rec.summary ? (
            <p className="text-[12px] text-[#374151] mt-1 leading-snug line-clamp-2">
              {rec.summary}
            </p>
          ) : null}
          {linkedWsCodes.length ? (
            <div
              className="mt-2 flex items-center gap-1.5 flex-wrap"
              data-testid={`primary-rec-linked-${rec.id}`}
            >
              {linkedWsCodes.slice(0, expanded ? linkedWsCodes.length : 6).map((c) => (
                <span
                  key={c}
                  className="text-[10px] font-bold tracking-wider text-[#025979] bg-[#F0F7FA] border border-[#BAE6FD] px-1.5 py-0.5 rounded-sm"
                >
                  {c}
                </span>
              ))}
              {!expanded && linkedWsCodes.length > 6 ? (
                <span className="text-[10px] text-[#6B7280]">
                  +{linkedWsCodes.length - 6} more
                </span>
              ) : null}
            </div>
          ) : null}
          <div className="mt-2 flex items-center gap-3 text-[11px] text-[#6B7280]">
            <span>
              <strong className="text-[#374151]">{linkedWsCodes.length}</strong>{" "}
              {linkedWsCodes.length === 1 ? "workstream" : "workstreams"}
            </span>
            <span>·</span>
            <span>
              <strong className="text-[#374151]">{findingCount}</strong>{" "}
              {findingCount === 1 ? "finding" : "findings"}
            </span>
            {rec.recommendation_type ? (
              <>
                <span>·</span>
                <span>{rec.recommendation_type}</span>
              </>
            ) : null}
            {rec.complexity ? (
              <>
                <span>·</span>
                <span>{rec.complexity}</span>
              </>
            ) : null}
          </div>
        </button>
        <div className="flex items-center gap-1 shrink-0">
          <button
            type="button"
            onClick={onToggle}
            className="h-7 w-7 inline-flex items-center justify-center text-[#025979] hover:bg-[#F0F7FA] rounded-sm"
            data-testid={`primary-rec-expand-${rec.id}`}
            aria-label={expanded ? "Collapse" : "Expand"}
          >
            {expanded ? (
              <ChevronUp className="w-4 h-4" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )}
          </button>
        </div>
      </header>

      {/* Expanded body */}
      {expanded ? (
        <PrimaryExpandedBody
          rec={rec}
          status={status}
          linkedWsList={linkedWsList}
          linkedFindings={linkedFindings}
          sitesById={sitesById}
          isFinalized={isFinalized}
          onEdit={onEdit}
          onStatus={onStatus}
          onClassify={onClassify}
        />
      ) : null}
    </article>
  );
}

function PrimaryExpandedBody({
  rec,
  status,
  linkedWsList,
  linkedFindings,
  sitesById,
  isFinalized,
  onEdit,
  onStatus,
  onClassify,
}) {
  const REC_TYPES = [
    "Config", "Enhancement", "Interface", "Workflow",
    "Report", "Conversion", "Mixed", "Strategic",
  ];
  const COMPLEXITIES = ["Low", "Medium", "High", "Very High"];
  return (
    <div className="border-t border-[#E5E7EB]">
      {/* Linked Workstreams */}
      <div className="px-4 py-3">
        <div className="flex items-center justify-between gap-2 mb-2">
          <h5 className="text-[11px] font-semibold tracking-[0.16em] uppercase text-[#025979]">
            Linked Workstreams
          </h5>
          {status !== "archived" ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={onEdit}
              disabled={isFinalized}
              className="h-7 px-2 text-[#025979] hover:bg-[#F0F7FA] rounded-sm text-xs"
              data-testid={`primary-rec-edit-${rec.id}`}
              title="Add or remove linked workstreams"
            >
              <Pencil className="w-3.5 h-3.5 mr-1" />
              Manage Workstreams
            </Button>
          ) : null}
        </div>
        {linkedWsList.length === 0 ? (
          <p className="text-xs text-[#6B7280] italic">
            No linked workstreams resolvable from current accepted set.
          </p>
        ) : (
          <div className="overflow-x-auto border border-[#E5E7EB] rounded-sm">
            <table
              className="w-full text-sm"
              data-testid={`primary-rec-ws-table-${rec.id}`}
            >
              <thead className="bg-[#F8FAFC] text-left text-[10px] uppercase tracking-wider text-[#6B7280]">
                <tr>
                  <th className="px-3 py-2 font-semibold">Workstream</th>
                  <th className="px-3 py-2 font-semibold min-w-[260px]">Title</th>
                  <th className="px-3 py-2 font-semibold">Status</th>
                  <th className="px-3 py-2 font-semibold text-right">Findings</th>
                </tr>
              </thead>
              <tbody>
                {linkedWsList.map((ws) => {
                  const wsStatus = (ws.status || "draft").toLowerCase();
                  return (
                    <tr key={ws.id} className="border-t border-[#F1F5F9]">
                      <td className="px-3 py-2">
                        <span className="inline-flex text-[10px] font-bold tracking-wider text-[#025979] bg-[#F0F7FA] border border-[#BAE6FD] px-1.5 py-0.5 rounded-sm whitespace-nowrap">
                          {ws.group_code}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-[13px] text-[#111827]">
                        {ws.title}
                      </td>
                      <td className="px-3 py-2">
                        <span
                          className={`inline-flex px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider border rounded-sm ${
                            STATUS_CHIP[wsStatus] || STATUS_CHIP.draft
                          }`}
                        >
                          {wsStatus}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-xs text-[#374151] text-right">
                        {(ws.linked_finding_ids || []).length}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Linked Findings (union, deduped) */}
      <div className="px-4 py-3 border-t border-[#E5E7EB]">
        <h5 className="text-[11px] font-semibold tracking-[0.16em] uppercase text-[#025979] mb-2">
          Linked Findings ({linkedFindings.length})
        </h5>
        {linkedFindings.length === 0 ? (
          <p className="text-xs text-[#6B7280] italic">
            No findings linked.
          </p>
        ) : (
          <div className="overflow-x-auto border border-[#E5E7EB] rounded-sm">
            <table
              className="w-full text-sm"
              data-testid={`primary-rec-findings-table-${rec.id}`}
            >
              <thead className="bg-[#F8FAFC] text-left text-[10px] uppercase tracking-wider text-[#6B7280]">
                <tr>
                  <th className="px-3 py-2 font-semibold">Finding ID</th>
                  <th className="px-3 py-2 font-semibold min-w-[260px]">Finding</th>
                  <th className="px-3 py-2 font-semibold">Priority</th>
                  <th className="px-3 py-2 font-semibold">Type</th>
                  <th className="px-3 py-2 font-semibold">Complexity</th>
                  <th className="px-3 py-2 font-semibold">Warehouses</th>
                </tr>
              </thead>
              <tbody>
                {linkedFindings.map((f) => {
                  const priority = f.priority || f.severity || "medium";
                  const wh = (f.applicable_site_ids || f.site_ids || [])
                    .map((sid) => sitesById?.[sid])
                    .filter(Boolean);
                  return (
                    <tr key={f.id} className="border-t border-[#F1F5F9]">
                      <td className="px-3 py-2">
                        <span className="inline-flex text-[10px] font-bold tracking-wider text-[#025979] bg-[#F0F7FA] border border-[#BAE6FD] px-1.5 py-0.5 rounded-sm whitespace-nowrap">
                          {f.finding_code || "—"}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-[13px] text-[#111827]">
                        {f.title}
                      </td>
                      <td className="px-3 py-2 text-xs capitalize text-[#374151]">
                        {priority}
                      </td>
                      <td
                        className="px-3 py-2 text-xs text-[#374151]"
                        data-testid={`primary-rec-ftype-${rec.id}-${f.id}`}
                      >
                        {f.ai_implementation_wricef_type || (
                          <span className="text-[#94A3B8] italic">Unclassified</span>
                        )}
                      </td>
                      <td
                        className="px-3 py-2 text-xs text-[#374151]"
                        data-testid={`primary-rec-fcomplexity-${rec.id}-${f.id}`}
                      >
                        {f.ai_implementation_complexity || (
                          <span className="text-[#94A3B8] italic">Unclassified</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs text-[#374151]">
                        {wh.length === 0 ? (
                          <span className="text-[#94A3B8]">—</span>
                        ) : wh.length <= 2 ? (
                          wh.join(", ")
                        ) : (
                          <span>
                            {wh.slice(0, 2).join(", ")}{" "}
                            <span className="text-[#94A3B8]">+{wh.length - 2}</span>
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Recommendation Details */}
      <div className="px-4 py-3 border-t border-[#E5E7EB] space-y-3">
        <h5 className="text-[11px] font-semibold tracking-[0.16em] uppercase text-[#025979]">
          Recommendation Details
        </h5>
        {rec.summary ? (
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[#6B7280] mb-1">
              Summary
            </div>
            <p className="text-[13px] text-[#374151] leading-snug whitespace-pre-wrap">
              {rec.summary}
            </p>
          </div>
        ) : null}
        {(rec.expected_business_impact || []).length ? (
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[#6B7280] mb-1">
              Expected Business Impact
            </div>
            <ul className="text-[12px] text-[#374151] list-disc list-inside space-y-0.5">
              {(rec.expected_business_impact || []).map((b, i) => (
                <li key={i}>{b}</li>
              ))}
            </ul>
          </div>
        ) : null}

        <div className="text-[10px] font-semibold tracking-[0.16em] uppercase text-[#025979]">
          Recommendation Classification
        </div>
        <div className="flex items-center gap-2 flex-wrap" data-testid={`primary-rec-classify-${rec.id}`}>
          <label className="text-[10px] uppercase tracking-wider text-[#6B7280]">
            Type
          </label>
          <Select
            value={rec.recommendation_type || "Config"}
            onValueChange={(v) => onClassify({ recommendation_type: v })}
            disabled={isFinalized}
          >
            <SelectTrigger
              className="h-7 w-32 rounded-sm text-[11px]"
              data-testid={`rec-type-${rec.id}`}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {REC_TYPES.map((t) => (
                <SelectItem key={t} value={t}>{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {(rec.override_fields || []).includes("recommendation_type") ? (
            <span
              data-testid={`rec-type-override-badge-${rec.id}`}
              className="inline-flex px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider border rounded-sm bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]"
              title="Consultant Override — will not be overwritten on regeneration."
            >
              Override
            </span>
          ) : null}
          <label className="text-[10px] uppercase tracking-wider text-[#6B7280] ml-2">
            Complexity
          </label>
          <Select
            value={rec.complexity || "Medium"}
            onValueChange={(v) => onClassify({ complexity: v })}
            disabled={isFinalized}
          >
            <SelectTrigger
              className="h-7 w-28 rounded-sm text-[11px]"
              data-testid={`rec-complexity-${rec.id}`}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {COMPLEXITIES.map((c) => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {(rec.override_fields || []).includes("complexity") ? (
            <span
              data-testid={`rec-complexity-override-badge-${rec.id}`}
              className="inline-flex px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider border rounded-sm bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]"
              title="Consultant Override — will not be overwritten on regeneration."
            >
              Override
            </span>
          ) : null}
        </div>
      </div>

      {/* Footer actions */}
      <footer className="px-4 py-3 border-t border-[#E5E7EB] bg-[#F8FAFC] flex items-center justify-end gap-2 flex-wrap">
        {status !== "accepted" && status !== "archived" ? (
          <Button
            onClick={() => onStatus("accepted")}
            disabled={isFinalized}
            className="h-7 bg-[#047857] hover:bg-[#065F46] text-white rounded-sm text-xs"
            data-testid={`primary-rec-accept-${rec.id}`}
          >
            <CheckCircle className="w-3.5 h-3.5 mr-1" />
            Accept
          </Button>
        ) : null}
        {status === "accepted" ? (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onStatus("draft")}
            disabled={isFinalized}
            className="h-7 rounded-sm text-xs border-[#E5E7EB] text-[#374151] hover:border-[#025979] hover:text-[#025979]"
            data-testid={`primary-rec-revert-${rec.id}`}
          >
            <Undo2 className="w-3.5 h-3.5 mr-1" />
            Revert to Draft
          </Button>
        ) : null}
        {status !== "archived" ? (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onStatus("archived")}
            disabled={isFinalized}
            className="h-7 rounded-sm text-xs border-[#E5E7EB] text-[#B91C1C] hover:border-[#B91C1C] hover:bg-[#FEF2F2]"
            data-testid={`primary-rec-archive-${rec.id}`}
          >
            <Archive className="w-3.5 h-3.5 mr-1" />
            Archive
          </Button>
        ) : (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onStatus("draft")}
            disabled={isFinalized}
            className="h-7 rounded-sm text-xs border-[#E5E7EB] text-[#374151] hover:border-[#025979] hover:text-[#025979]"
          >
            Restore
          </Button>
        )}
      </footer>
    </div>
  );
}


// ============================================================ Section B --
function StrategicSection({
  items,
  workstreams,
  workstreamsById,
  findingsById,
  sitesById,
  isFinalized,
  onAdd,
  onEdit,
  onDelete,
  onStatus,
  onClassify,
}) {
  const [expandedId, setExpandedId] = useState(null);
  void workstreams;
  return (
    <section data-testid="strategic-recommendations-section">
      <SectionHeader
        icon={Lightbulb}
        label="Strategic / Accelerator Recommendations"
        meta={`${items.length} ${items.length === 1 ? "item" : "items"}`}
        helper="Cross-workstream transformation accelerators (optional)"
        right={
          <Button
            variant="outline"
            onClick={onAdd}
            disabled={isFinalized}
            className="h-7 rounded-sm text-xs border-[#025979] text-[#025979] hover:bg-[#F0F7FA]"
            data-testid="strategic-add-btn"
          >
            <Plus className="w-3.5 h-3.5 mr-1" />
            Add Strategic Recommendation
          </Button>
        }
      />
      {items.length === 0 ? (
        <div
          className="bg-white border border-dashed border-[#CBD5E1] rounded-sm px-5 py-6 text-center"
          data-testid="strategic-empty-state"
        >
          <p className="text-xs text-[#6B7280]">
            No strategic recommendations captured yet. Use this section for
            cross-workstream accelerators like SmartRF, SmartInspect, or a
            Warehouse Governance Program.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((s) => (
            <StrategicCard
              key={s.id}
              row={s}
              expanded={expandedId === s.id}
              onToggle={() =>
                setExpandedId(expandedId === s.id ? null : s.id)
              }
              workstreamsById={workstreamsById}
              findingsById={findingsById}
              sitesById={sitesById}
              isFinalized={isFinalized}
              onEdit={() => onEdit(s)}
              onDelete={() => onDelete(s)}
              onStatus={(status) => onStatus(s, status)}
              onClassify={(patch) => onClassify(s, patch)}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function StrategicCard({
  row,
  expanded,
  onToggle,
  workstreamsById,
  findingsById,
  sitesById,
  isFinalized,
  onEdit,
  onDelete,
  onStatus,
  onClassify,
}) {
  const status = (row.status || "draft").toLowerCase();
  const COMPLEXITIES = ["Low", "Medium", "High", "Very High"];

  const supportedIds = row.supported_workstream_ids || [];
  const supportedList = supportedIds
    .map((id) => workstreamsById?.[id])
    .filter(Boolean);
  const supportedCodes = supportedList.length
    ? supportedList.map((w) => w.group_code).filter(Boolean)
    : (row.supported_workstream_codes || []);

  // Union of linked findings across all supported workstreams (read-only).
  const linkedFindings = useMemo(() => {
    const seen = new Set();
    const out = [];
    supportedList.forEach((ws) => {
      (ws.linked_finding_ids || []).forEach((fid) => {
        if (!fid || seen.has(fid)) return;
        seen.add(fid);
        const f = findingsById?.[fid];
        if (f) out.push(f);
      });
    });
    out.sort(compareFindings);
    return out;
  }, [supportedList, findingsById]);

  return (
    <article
      className={`bg-white border rounded-sm transition ${
        expanded
          ? "border-[#9D174D] shadow-sm"
          : "border-[#E5E7EB] hover:border-[#94A3B8]"
      }`}
      data-testid={`strategic-rec-${row.id}`}
    >
      <header className="px-4 py-3 flex items-start gap-3">
        <span className="shrink-0 text-[10px] font-bold tracking-wider text-[#9D174D] bg-[#FCE7F3] border border-[#FBCFE8] px-1.5 py-0.5 rounded-sm whitespace-nowrap mt-0.5">
          {row.srec_code}
        </span>
        <button
          type="button"
          onClick={onToggle}
          className="flex-1 min-w-0 text-left"
          data-testid={`strategic-rec-toggle-${row.id}`}
        >
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="text-sm font-semibold text-[#111827]">{row.title}</h4>
            <span
              className={`inline-flex px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider border rounded-sm ${
                STATUS_CHIP[status] || STATUS_CHIP.draft
              }`}
              data-testid={`strategic-rec-status-${row.id}`}
            >
              {status}
            </span>
            <span className="inline-flex px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider border rounded-sm bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]">
              Strategic
            </span>
            {row.suggested_solution ? (
              <span
                className="inline-flex px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider border rounded-sm bg-[#EEF2FF] text-[#3730A3] border-[#C7D2FE]"
                title="Suggested WarehouseX accelerator"
                data-testid={`strategic-rec-solution-${row.id}`}
              >
                {row.suggested_solution}
              </span>
            ) : null}
          </div>
          {row.summary ? (
            <p className="text-[12px] text-[#374151] mt-1 leading-snug line-clamp-2">
              {row.summary}
            </p>
          ) : null}
          {supportedCodes.length ? (
            <div className="mt-2 flex items-center gap-1.5 flex-wrap">
              <span className="text-[10px] uppercase tracking-wider text-[#6B7280]">
                Supports:
              </span>
              {supportedCodes.slice(0, expanded ? supportedCodes.length : 6).map((c) => (
                <span
                  key={c}
                  className="text-[10px] font-bold tracking-wider text-[#025979] bg-[#F0F7FA] border border-[#BAE6FD] px-1.5 py-0.5 rounded-sm"
                >
                  {c}
                </span>
              ))}
              {!expanded && supportedCodes.length > 6 ? (
                <span className="text-[10px] text-[#6B7280]">
                  +{supportedCodes.length - 6} more
                </span>
              ) : null}
            </div>
          ) : null}
          <div className="mt-2 flex items-center gap-3 text-[11px] text-[#6B7280]">
            <span>
              <strong className="text-[#374151]">{supportedCodes.length}</strong>{" "}
              {supportedCodes.length === 1 ? "workstream" : "workstreams"}
            </span>
            {row.complexity ? (
              <>
                <span>·</span>
                <span>{row.complexity}</span>
              </>
            ) : null}
          </div>
        </button>
        <div className="flex items-center gap-1 shrink-0">
          {status !== "archived" ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={onEdit}
              disabled={isFinalized}
              className="h-7 px-2 text-[#9D174D] hover:bg-[#FCE7F3] rounded-sm text-xs"
              data-testid={`strategic-edit-${row.id}`}
            >
              <Pencil className="w-3.5 h-3.5 mr-1" />
              Manage Workstreams
            </Button>
          ) : null}
          <button
            type="button"
            onClick={onToggle}
            className="h-7 w-7 inline-flex items-center justify-center text-[#9D174D] hover:bg-[#FCE7F3] rounded-sm"
            data-testid={`strategic-rec-expand-${row.id}`}
            aria-label={expanded ? "Collapse" : "Expand"}
          >
            {expanded ? (
              <ChevronUp className="w-4 h-4" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )}
          </button>
        </div>
      </header>

      {expanded ? (
        <div className="border-t border-[#E5E7EB]">
          {/* Supported Workstreams */}
          <div className="px-4 py-3">
            <h5 className="text-[11px] font-semibold tracking-[0.16em] uppercase text-[#9D174D] mb-2">
              Supported Workstreams
            </h5>
            {supportedList.length === 0 ? (
              <p className="text-xs text-[#6B7280] italic">
                No supported workstreams resolvable.
              </p>
            ) : (
              <div className="overflow-x-auto border border-[#E5E7EB] rounded-sm">
                <table className="w-full text-sm">
                  <thead className="bg-[#F8FAFC] text-left text-[10px] uppercase tracking-wider text-[#6B7280]">
                    <tr>
                      <th className="px-3 py-2 font-semibold">Workstream</th>
                      <th className="px-3 py-2 font-semibold min-w-[260px]">Title</th>
                      <th className="px-3 py-2 font-semibold">Status</th>
                      <th className="px-3 py-2 font-semibold text-right">Findings</th>
                    </tr>
                  </thead>
                  <tbody>
                    {supportedList.map((ws) => {
                      const wsStatus = (ws.status || "draft").toLowerCase();
                      return (
                        <tr key={ws.id} className="border-t border-[#F1F5F9]">
                          <td className="px-3 py-2">
                            <span className="inline-flex text-[10px] font-bold tracking-wider text-[#025979] bg-[#F0F7FA] border border-[#BAE6FD] px-1.5 py-0.5 rounded-sm whitespace-nowrap">
                              {ws.group_code}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-[13px] text-[#111827]">
                            {ws.title}
                          </td>
                          <td className="px-3 py-2">
                            <span
                              className={`inline-flex px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider border rounded-sm ${
                                STATUS_CHIP[wsStatus] || STATUS_CHIP.draft
                              }`}
                            >
                              {wsStatus}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-xs text-[#374151] text-right">
                            {(ws.linked_finding_ids || []).length}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Linked Findings (derived) */}
          {linkedFindings.length ? (
            <div className="px-4 py-3 border-t border-[#E5E7EB]">
              <h5 className="text-[11px] font-semibold tracking-[0.16em] uppercase text-[#9D174D] mb-2">
                Linked Findings ({linkedFindings.length})
              </h5>
              <div className="overflow-x-auto border border-[#E5E7EB] rounded-sm">
                <table className="w-full text-sm">
                  <thead className="bg-[#F8FAFC] text-left text-[10px] uppercase tracking-wider text-[#6B7280]">
                    <tr>
                      <th className="px-3 py-2 font-semibold">Finding ID</th>
                      <th className="px-3 py-2 font-semibold min-w-[260px]">Finding</th>
                      <th className="px-3 py-2 font-semibold">Priority</th>
                      <th className="px-3 py-2 font-semibold">Type</th>
                      <th className="px-3 py-2 font-semibold">Complexity</th>
                      <th className="px-3 py-2 font-semibold">Warehouses</th>
                    </tr>
                  </thead>
                  <tbody>
                    {linkedFindings.map((f) => {
                      const priority = f.priority || f.severity || "medium";
                      const wh = (f.applicable_site_ids || f.site_ids || [])
                        .map((sid) => sitesById?.[sid])
                        .filter(Boolean);
                      return (
                        <tr key={f.id} className="border-t border-[#F1F5F9]">
                          <td className="px-3 py-2">
                            <span className="inline-flex text-[10px] font-bold tracking-wider text-[#025979] bg-[#F0F7FA] border border-[#BAE6FD] px-1.5 py-0.5 rounded-sm whitespace-nowrap">
                              {f.finding_code || "—"}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-[13px] text-[#111827]">
                            {f.title}
                          </td>
                          <td className="px-3 py-2 text-xs capitalize text-[#374151]">
                            {priority}
                          </td>
                          <td
                            className="px-3 py-2 text-xs text-[#374151]"
                            data-testid={`strategic-rec-ftype-${row.id}-${f.id}`}
                          >
                            {f.ai_implementation_wricef_type || (
                              <span className="text-[#94A3B8] italic">Unclassified</span>
                            )}
                          </td>
                          <td
                            className="px-3 py-2 text-xs text-[#374151]"
                            data-testid={`strategic-rec-fcomplexity-${row.id}-${f.id}`}
                          >
                            {f.ai_implementation_complexity || (
                              <span className="text-[#94A3B8] italic">Unclassified</span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-xs text-[#374151]">
                            {wh.length === 0 ? (
                              <span className="text-[#94A3B8]">—</span>
                            ) : wh.length <= 2 ? (
                              wh.join(", ")
                            ) : (
                              <span>
                                {wh.slice(0, 2).join(", ")}{" "}
                                <span className="text-[#94A3B8]">+{wh.length - 2}</span>
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}

          {/* Strategic Details */}
          <div className="px-4 py-3 border-t border-[#E5E7EB] space-y-3">
            <h5 className="text-[11px] font-semibold tracking-[0.16em] uppercase text-[#9D174D]">
              Strategic Details
            </h5>
            {row.why_strategic ? (
              <div>
                <div className="text-[10px] uppercase tracking-wider text-[#6B7280] mb-1">
                  Why Strategic
                </div>
                <p className="text-[12px] text-[#374151] whitespace-pre-wrap">
                  {row.why_strategic}
                </p>
              </div>
            ) : null}
            {row.expected_benefit ? (
              <div>
                <div className="text-[10px] uppercase tracking-wider text-[#6B7280] mb-1">
                  Expected Transformation Benefit
                </div>
                <p className="text-[12px] text-[#374151] whitespace-pre-wrap">
                  {row.expected_benefit}
                </p>
              </div>
            ) : null}
            {row.suggested_solution ? (
              <div>
                <div className="text-[10px] uppercase tracking-wider text-[#6B7280] mb-1">
                  Suggested Solution
                </div>
                <p className="text-[12px] text-[#374151]">{row.suggested_solution}</p>
              </div>
            ) : null}

            <div
              className="flex items-center gap-2 flex-wrap"
              data-testid={`strategic-rec-classify-${row.id}`}
            >
              <label className="text-[10px] uppercase tracking-wider text-[#6B7280]">
                Complexity
              </label>
              <Select
                value={row.complexity || "High"}
                onValueChange={(v) => onClassify && onClassify({ complexity: v })}
                disabled={isFinalized}
              >
                <SelectTrigger
                  className="h-7 w-28 rounded-sm text-[11px]"
                  data-testid={`srec-complexity-${row.id}`}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COMPLEXITIES.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {(row.override_fields || []).includes("complexity") ? (
                <span
                  data-testid={`srec-complexity-override-badge-${row.id}`}
                  className="inline-flex px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider border rounded-sm bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]"
                  title="Consultant Override — will not be overwritten on regeneration."
                >
                  Override
                </span>
              ) : null}
            </div>
          </div>

          {/* Footer actions */}
          <footer className="px-4 py-3 border-t border-[#E5E7EB] bg-[#F8FAFC] flex items-center justify-end gap-2 flex-wrap">
            {status !== "accepted" ? (
              <Button
                onClick={() => onStatus("accepted")}
                disabled={isFinalized}
                className="h-7 bg-[#047857] hover:bg-[#065F46] text-white rounded-sm text-xs"
                data-testid={`strategic-accept-${row.id}`}
              >
                <CheckCircle className="w-3.5 h-3.5 mr-1" />
                Accept
              </Button>
            ) : null}
            {status === "accepted" ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onStatus("draft")}
                disabled={isFinalized}
                className="h-7 rounded-sm text-xs border-[#E5E7EB] text-[#374151] hover:border-[#025979] hover:text-[#025979]"
                data-testid={`strategic-revert-${row.id}`}
              >
                <Undo2 className="w-3.5 h-3.5 mr-1" />
                Revert to Draft
              </Button>
            ) : null}
            <Button
              variant="outline"
              size="sm"
              onClick={onDelete}
              disabled={isFinalized}
              className="h-7 rounded-sm text-xs border-[#E5E7EB] text-[#B91C1C] hover:border-[#B91C1C] hover:bg-[#FEF2F2]"
              data-testid={`strategic-delete-${row.id}`}
            >
              <Trash2 className="w-3.5 h-3.5 mr-1" />
              Delete
            </Button>
          </footer>
        </div>
      ) : null}
    </article>
  );
}

// ============================================================== helpers --
function SectionHeader({ icon: Icon, label, meta, helper, right }) {
  return (
    <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
      <div className="flex items-center gap-2 min-w-0">
        {Icon ? <Icon className="w-4 h-4 text-[#025979] shrink-0" /> : null}
        <h2
          className="text-sm font-semibold text-[#111827]"
          style={{ fontFamily: "Chivo, sans-serif" }}
        >
          {label}
        </h2>
        {meta ? (
          <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-[#F1F5F9] text-[#475569]">
            {meta}
          </span>
        ) : null}
        {helper ? (
          <span className="text-[11px] text-[#6B7280] hidden md:inline">
            · {helper}
          </span>
        ) : null}
      </div>
      {right || null}
    </div>
  );
}

function EmptyState({ icon: Icon, title, description }) {
  return (
    <div
      className="bg-white border border-dashed border-[#CBD5E1] rounded-sm px-6 py-12 text-center"
      data-testid="recs-empty-state"
    >
      <div className="w-10 h-10 rounded-full bg-[#F0EDF9] mx-auto mb-3 flex items-center justify-center">
        <Icon className="w-5 h-5 text-[#6E5BCE]" />
      </div>
      <p className="text-sm font-semibold text-[#111827]">{title}</p>
      <p className="text-xs text-[#6B7280] mt-1 max-w-md mx-auto">
        {description}
      </p>
      <p
        className="mt-4 text-[10px] uppercase tracking-[0.18em] text-[#473F8B] font-semibold"
        data-testid="recs-empty-powered-by"
      >
        Powered by PRIVA
      </p>
    </div>
  );
}

// ============================================================== dialogs --
function EditPrimaryDialog({ rec, workstreams, onClose, onSave }) {
  const [title, setTitle] = useState(rec.title || "");
  const [summary, setSummary] = useState(rec.summary || "");
  const [impact, setImpact] = useState(
    (rec.expected_business_impact || []).join("\n")
  );
  // Consolidated recommendation support — every selected workstream
  // is persisted. Legacy single ``linked_workstream_id`` is normalised
  // into the same array shape so old recs without the plural field
  // still pre-select correctly.
  const initialIds = useMemo(() => {
    const ids = Array.isArray(rec.linked_workstream_ids)
      ? rec.linked_workstream_ids
      : [];
    if (ids.length) return [...new Set(ids)];
    return rec.linked_workstream_id ? [rec.linked_workstream_id] : [];
  }, [rec]);
  const [selectedIds, setSelectedIds] = useState(initialIds);

  const toggleWs = (wsId) => {
    setSelectedIds((prev) =>
      prev.includes(wsId)
        ? prev.filter((id) => id !== wsId)
        : [...prev, wsId]
    );
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-2xl max-h-[80vh] overflow-y-auto"
        data-testid="primary-rec-edit-dialog"
      >
        <DialogHeader>
          <DialogTitle>
            Edit {rec.rec_code} — {rec.title}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <Field label="Title">
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="h-8 text-sm rounded-sm"
              data-testid="primary-rec-edit-title"
            />
          </Field>
          <Field label="Summary">
            <Textarea
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              rows={4}
              className="text-sm rounded-sm"
              data-testid="primary-rec-edit-summary"
            />
          </Field>
          <Field label="Expected Business Impact (one bullet per line)">
            <Textarea
              value={impact}
              onChange={(e) => setImpact(e.target.value)}
              rows={5}
              className="text-sm rounded-sm font-mono"
              data-testid="primary-rec-edit-impact"
            />
          </Field>
          <Field label={`Linked Workstreams (${selectedIds.length} selected)`}>
            {selectedIds.length === 0 ? (
              <p className="text-[11px] text-[#B91C1C] mb-2">
                At least one workstream must be selected.
              </p>
            ) : null}
            <div
              className="max-h-56 overflow-y-auto border border-[#E5E7EB] rounded-sm divide-y divide-[#F1F5F9]"
              data-testid="primary-rec-edit-workstreams"
            >
              {workstreams.length === 0 ? (
                <p className="text-xs text-[#6B7280] italic px-3 py-2">
                  No workstreams available.
                </p>
              ) : (
                workstreams.map((w) => {
                  const checked = selectedIds.includes(w.id);
                  return (
                    <label
                      key={w.id}
                      className={`flex items-start gap-2 px-3 py-2 cursor-pointer text-xs transition-colors ${
                        checked
                          ? "bg-[#F0F7FA]"
                          : "bg-white hover:bg-[#F8FAFC]"
                      }`}
                      data-testid={`primary-rec-edit-ws-${w.group_code}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleWs(w.id)}
                        className="mt-0.5 accent-[#025979]"
                        data-testid={`primary-rec-edit-ws-checkbox-${w.group_code}`}
                      />
                      <span className="flex-1 min-w-0">
                        <span className="font-semibold text-[#025979]">
                          {w.group_code}
                        </span>
                        <span className="text-[#374151]"> — {w.title}</span>
                      </span>
                    </label>
                  );
                })
              )}
            </div>
          </Field>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            className="h-7 rounded-sm text-xs"
          >
            Cancel
          </Button>
          <Button
            onClick={() =>
              onSave({
                title,
                summary,
                expected_business_impact: impact
                  .split("\n")
                  .map((s) => s.trim())
                  .filter(Boolean),
                linked_workstream_ids: selectedIds,
              })
            }
            disabled={!title.trim() || selectedIds.length === 0}
            className="h-7 bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-xs"
            data-testid="primary-rec-edit-save"
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StrategicDialog({ mode, row, workstreams, onClose, onSave }) {
  const [title, setTitle] = useState(row?.title || "");
  const [summary, setSummary] = useState(row?.summary || "");
  const [benefit, setBenefit] = useState(row?.expected_benefit || "");
  const [supported, setSupported] = useState(row?.supported_workstream_ids || []);

  const toggleWs = (id) =>
    setSupported((cur) =>
      cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]
    );

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-2xl max-h-[80vh] overflow-y-auto"
        data-testid="strategic-dialog"
      >
        <DialogHeader>
          <DialogTitle>
            {mode === "edit" ? "Edit" : "Add"} Strategic Recommendation
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <Field label="Title">
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. SmartRF Mobility Upgrade"
              className="h-8 text-sm rounded-sm"
              data-testid="strategic-dialog-title"
            />
          </Field>
          <Field label="Summary">
            <Textarea
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              rows={3}
              className="text-sm rounded-sm"
              data-testid="strategic-dialog-summary"
            />
          </Field>
          <Field label="Expected Transformation Benefit">
            <Textarea
              value={benefit}
              onChange={(e) => setBenefit(e.target.value)}
              rows={3}
              className="text-sm rounded-sm"
              data-testid="strategic-dialog-benefit"
            />
          </Field>
          <Field label="Supports Workstreams (multi-select)">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 max-h-44 overflow-y-auto border border-[#E5E7EB] rounded-sm p-2 bg-[#F8FAFC]">
              {workstreams.length === 0 ? (
                <p className="text-[11px] text-[#6B7280] italic">
                  No workstreams available.
                </p>
              ) : (
                workstreams.map((w) => {
                  const checked = supported.includes(w.id);
                  return (
                    <label
                      key={w.id}
                      className={`flex items-center gap-2 px-2 py-1 text-xs rounded-sm cursor-pointer border ${
                        checked
                          ? "bg-[#F0F7FA] border-[#BAE6FD] text-[#025979]"
                          : "bg-white border-[#E5E7EB] text-[#374151]"
                      }`}
                      data-testid={`strategic-ws-${w.id}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleWs(w.id)}
                        className="h-3 w-3"
                      />
                      <span className="text-[10px] font-bold">{w.group_code}</span>
                      <span className="truncate">{w.title}</span>
                    </label>
                  );
                })
              )}
            </div>
          </Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="h-7 rounded-sm text-xs">
            Cancel
          </Button>
          <Button
            onClick={() =>
              onSave({
                title,
                summary,
                expected_benefit: benefit,
                supported_workstream_ids: supported,
              })
            }
            disabled={!title.trim()}
            className="h-7 bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-xs"
            data-testid="strategic-dialog-save"
          >
            {mode === "edit" ? "Save" : "Add"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-[#6B7280]">
        {label}
      </label>
      <div className="mt-1">{children}</div>
    </div>
  );
}


// =================================================== Stage 2 Generate Band --
function GenerateBand({
  accepted,
  totalWorkstreams,
  existingPrimary,
  existingStrategic,
  generating,
  isFinalized,
  onGenerate,
}) {
  const disabled = generating || isFinalized || accepted === 0;
  const hasExisting = existingPrimary + existingStrategic > 0;
  return (
    <section
      className="bg-white border border-[#E5E7EB] rounded-sm px-4 py-3 flex items-center justify-between gap-3 flex-wrap"
      data-testid="recommendations-generate-band"
    >
      <div className="flex items-center gap-2 min-w-0">
        <Sparkles className="w-4 h-4 text-[#6E5BCE] shrink-0" />
        <h2
          className="text-sm font-semibold text-[#111827]"
          style={{ fontFamily: "Chivo, sans-serif" }}
        >
          Generate Recommendations
        </h2>
        <span
          className="text-[10px] px-1.5 py-0.5 rounded-sm bg-[#ECFDF5] text-[#047857] border border-[#A7F3D0]"
          data-testid="recs-accepted-count"
          title="Accepted workstreams ready to feed recommendations"
        >
          {accepted} accepted
        </span>
        <span
          className="text-[10px] px-1.5 py-0.5 rounded-sm bg-[#F1F5F9] text-[#475569]"
          title="All non-archived workstreams"
        >
          / {totalWorkstreams} workstreams
        </span>
        <span className="text-[11px] text-[#6B7280] hidden md:inline truncate ml-1">
          {accepted === 0
            ? "· Accept at least one workstream in Improvement Insights to enable generation."
            : "· Generates one Primary Recommendation per Accepted workstream."}
        </span>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Button
          onClick={onGenerate}
          disabled={disabled}
          className="h-7 bg-[#6E5BCE] hover:bg-[#5847B0] disabled:bg-[#CBD5E1] disabled:cursor-not-allowed text-white rounded-sm text-xs transition-colors"
          data-testid="recommendations-generate-btn"
          title={
            accepted === 0
              ? "Accept a workstream in Improvement Insights first"
              : hasExisting
              ? "Re-generate — replaces all current recommendations"
              : "Generate recommendations from accepted workstreams"
          }
        >
          <RefreshCw
            className={`w-3.5 h-3.5 mr-1 ${generating ? "animate-spin" : ""}`}
          />
          {generating
            ? "Generating…"
            : hasExisting
            ? "Re-generate Recommendations"
            : "Generate Recommendations"}
        </Button>
      </div>
    </section>
  );
}

function RegenerateRecsConfirmDialog({
  accepted,
  primary,
  strategic,
  onClose,
  onConfirm,
}) {
  const totalExisting = primary + strategic;
  return (    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-lg"
        data-testid="recs-regen-confirm-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-[#B45309]" />
            Re-generate Recommendations?
          </DialogTitle>
          <DialogDescription>
            {totalExisting > 0
              ? `Replaces every primary and strategic recommendation on this engagement with one freshly composed recommendation per accepted workstream (${accepted}).`
              : `One Primary Recommendation will be generated for each of the ${accepted} accepted workstream${accepted === 1 ? "" : "s"}.`}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm text-[#374151]">
          <p>This will:</p>
          <ul className="text-xs space-y-1 pl-1">
            <li className="flex items-center gap-2">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#B91C1C]" />
              delete existing AI-generated recommendations
            </li>
            <li className="flex items-center gap-2">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#025979]" />
              replace primary recommendations ({primary} current)
            </li>
            <li className="flex items-center gap-2">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#9D174D]" />
              replace strategic recommendations ({strategic} current)
            </li>
          </ul>
          <p className="text-xs text-[#047857] bg-[#ECFDF5] border border-[#A7F3D0] rounded-sm px-3 py-2">
            Accepted workstreams will <strong>NOT</strong> be modified. One
            primary recommendation will be produced per accepted workstream
            ({accepted} total).
          </p>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            className="h-7 rounded-sm text-xs"
            data-testid="recs-regen-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            className="h-7 bg-[#6E5BCE] hover:bg-[#5847B0] text-white rounded-sm text-xs"
            data-testid="recs-regen-confirm"
          >
            <RefreshCw className="w-3.5 h-3.5 mr-1" />
            {totalExisting > 0 ? "Replace & Re-generate" : "Generate"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
